import { PortalReadState } from '../portal-read-state.tsx';
import { WorkPage, WorkSection } from '../../components/work-page.tsx';
import '../portal-page.css';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFeedbackApi, AdminApiError } from '../../api/admin.ts';
import { Alert, Button, LoadingBlock, Select, TextArea, TextInput } from '../../components/ui.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';
import { formatCents } from '@shared/calculations/pricing.ts';
import {
  FEEDBACK_CATEGORIES,
  FEEDBACK_REJECTION_REASONS,
  FEEDBACK_STATUS_TRANSITIONS,
} from '@shared/constants/feedback.ts';
import type { FeedbackStatus } from '@shared/constants/feedback.ts';
import { formatInstantDateTime, formatNumericInstant } from '../../i18n/datetime.ts';

/**
 * One feature request, in the operator console (spec §12, §17–§19, §27).
 *
 * Laid out in the order somebody actually works: the submission, then what the
 * board already contains that looks like it, then the company's participation
 * record, then the buttons. Deciding before seeing the duplicates is how a
 * board fills up with the same idea five times, so the duplicates come first.
 *
 * Every action posts and re-reads rather than patching local state: these are
 * decisions with an audit trail behind them, and a screen that optimistically
 * shows "approved" while the server said 409 is worse than one that waits.
 */
export default function AdminFeedbackDetailPage() {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['admin', 'feedback', requestId],
    queryFn: ({ signal }) => adminFeedbackApi.detail(requestId!, signal),
    enabled: Boolean(requestId),
  });

  const request = query.data;
  useDocumentTitle(request?.title ?? 'Product Feedback');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] });
  };

  async function run(action: () => Promise<unknown>, message: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
      setNotice(message);
      refresh();
    } catch (actionError) {
      setError(actionError instanceof AdminApiError ? actionError.message : String(actionError));
    } finally {
      setBusy(false);
    }
  }

  if (query.isLoading) return <PortalReadState title="Product Feedback" label="Loading request…" />;
  if (query.isError || !request) {
    return (
      <PortalReadState title="Product Feedback" error="That request could not be loaded." retry={() => query.refetch()}>
        <Link to="/admin/feedback" className="font-semibold underline">
          Back to Product Feedback
        </Link>
      </PortalReadState>
    );
  }

  const allowed = FEEDBACK_STATUS_TRANSITIONS[request.status] ?? [];

  return (
    <div className="portal-page">
      <WorkPage
        title={request.title}
        description={`${request.status.replace(/_/g, ' ')} · submitted ${formatNumericInstant(request.createdAt)}`}
        actions={
          <Link className="btn" to="/admin/feedback">
            Back to queue
          </Link>
        }
      >
        {error ? (
          <div className="mb-4">
            <Alert tone="error" title="That action failed">
              {error}
            </Alert>
          </div>
        ) : null}
        {notice ? (
          <div className="mb-4">
            <Alert tone="success" title="Done">
              {notice}
            </Alert>
          </div>
        ) : null}

        <WorkSection title="The submission">
          <p className="mt-3 whitespace-pre-wrap text-sm">{request.description}</p>
          <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
            <Detail label="Company">{request.companyName ?? request.companyId}</Detail>
            <Detail label="Submitted by">{request.submittedByName ?? '—'}</Detail>
            <Detail label="Email">{request.submittedByEmail ?? '—'}</Detail>
            <Detail label="Product area">{request.category?.replace(/_/g, ' ') ?? 'not set'}</Detail>
            <Detail label="Votes">
              {request.votes} from {request.companiesVoting} companies · {formatCents(request.voterMrrCents, 'USD')} MRR
            </Detail>
            <Detail label="Recent votes">
              {request.votesRecent7d} in 7 days · {request.votesRecent30d} in 30 days
            </Detail>
          </dl>
        </WorkSection>

        <WorkSection title="This company's participation">
          <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-3">
            <Detail label="Submissions this week">{request.submitter.submissionsThisWeek}</Detail>
            <Detail label="Submissions all time">{request.submitter.submissionsAllTime}</Detail>
            <Detail label="Votes all time">{request.submitter.votesAllTime}</Detail>
            <Detail label="Qualifying votes this month">{request.submitter.votesThisMonth}</Detail>
            <Detail label="Plan">{request.submitter.companyPlan ?? '—'}</Detail>
            <Detail label="MRR">{formatCents(request.submitter.companyMrrCents, 'USD')}</Detail>
          </dl>
        </WorkSection>

        {request.assistant?.available ? (
          <WorkSection title="Assistant suggestions">
            <p className="mt-1 text-xs text-[color:var(--color-muted)]">
              Advisory only. Nothing here is applied automatically — you decide.
            </p>
            <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
              <Detail label="Suggested category">{request.assistant.suggestedCategory ?? '—'}</Detail>
              <Detail label="Spam likelihood">{request.assistant.spamLikelihood ?? '—'}</Detail>
              <Detail label="Suggested title">{request.assistant.suggestedTitle ?? '—'}</Detail>
              <Detail label="Note">{request.assistant.note ?? '—'}</Detail>
            </dl>
          </WorkSection>
        ) : null}

        {request.duplicates.length > 0 ? (
          <WorkSection title="Possible duplicates">
            <ul className="mt-3 grid gap-2 text-sm">
              {request.duplicates.map((duplicate) => (
                <li key={duplicate.id} className="flex flex-wrap items-center justify-between gap-2">
                  <span>
                    <Link to={`/admin/feedback/${duplicate.id}`} className="font-medium hover:underline">
                      {duplicate.title}
                    </Link>
                    <span className="ml-2 text-[color:var(--color-muted)]">
                      {duplicate.similarity}% similar · {duplicate.votes} votes · {duplicate.status.replace(/_/g, ' ')}
                    </span>
                  </span>
                  <Button
                    disabled={busy}
                    onClick={() =>
                      run(
                        () => adminFeedbackApi.merge(request.id, { canonicalRequestId: duplicate.id }),
                        'Merged. Votes and followers moved across.',
                      )
                    }
                  >
                    Merge into this
                  </Button>
                </li>
              ))}
            </ul>
          </WorkSection>
        ) : null}

        {request.mergedInto.length > 0 ? (
          <WorkSection title="Merged into this request">
            <ul className="mt-3 grid gap-1 text-sm">
              {request.mergedInto.map((item) => (
                <li key={item.id}>
                  <Link to={`/admin/feedback/${item.id}`} className="hover:underline">
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>
          </WorkSection>
        ) : null}

        {request.status === 'pending_moderation' ? <ModerationPanel request={request} busy={busy} onRun={run} /> : null}

        <LifecyclePanel request={request} allowed={allowed} busy={busy} onRun={run} onNavigate={navigate} />

        {request.previewResponses.length > 0 ? (
          <WorkSection title="Preview responses">
            <p className="mt-1 text-xs text-[color:var(--color-muted)]">
              {request.previewSessions} preview sessions opened.
            </p>
            <ul className="mt-3 grid gap-3 text-sm">
              {request.previewResponses.map((response) => (
                <li key={response.id} className="border-l-4 border-[color:var(--color-line)] pl-3">
                  <p className="font-semibold">{response.rating}</p>
                  {response.feedback ? <p className="mt-1 whitespace-pre-wrap">{response.feedback}</p> : null}
                  <p className="mt-1 text-xs text-[color:var(--color-muted)]">
                    {formatNumericInstant(response.createdAt)}
                  </p>
                </li>
              ))}
            </ul>
          </WorkSection>
        ) : null}

        <WorkSection title="History">
          <ol className="mt-3 grid gap-2 text-sm">
            {request.statusHistory.map((entry) => (
              <li key={entry.id}>
                <span className="font-medium">
                  {entry.oldStatus ? `${entry.oldStatus.replace(/_/g, ' ')} → ` : ''}
                  {entry.newStatus.replace(/_/g, ' ')}
                </span>
                <span className="ml-2 text-[color:var(--color-muted)]">
                  {formatInstantDateTime(entry.createdAt)} · {entry.changedByEmail ?? 'system'}
                </span>
                {entry.publicNote ? (
                  <p className="text-[color:var(--color-muted)]">Public: {entry.publicNote}</p>
                ) : null}
                {entry.internalNote ? (
                  <p className="text-[color:var(--color-muted)]">Internal: {entry.internalNote}</p>
                ) : null}
              </li>
            ))}
          </ol>

          {request.moderationActions.length > 0 ? (
            <>
              <h3 className="mt-5 font-semibold">Moderation</h3>
              <ol className="mt-2 grid gap-2 text-sm">
                {request.moderationActions.map((entry) => (
                  <li key={entry.id}>
                    <span className="font-medium">{entry.action.replace(/_/g, ' ')}</span>
                    <span className="ml-2 text-[color:var(--color-muted)]">
                      {formatInstantDateTime(entry.createdAt)} · {entry.moderatorEmail}
                      {entry.reason ? ` · ${entry.reason.replace(/_/g, ' ')}` : ''}
                    </span>
                    {entry.customerMessage ? (
                      <p className="text-[color:var(--color-muted)]">Told the customer: {entry.customerMessage}</p>
                    ) : null}
                    {entry.notes ? <p className="text-[color:var(--color-muted)]">Notes: {entry.notes}</p> : null}
                  </li>
                ))}
              </ol>
            </>
          ) : null}
        </WorkSection>
      </WorkPage>
    </div>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-[color:var(--color-muted)]">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

type Runner = (action: () => Promise<unknown>, message: string) => Promise<void>;
type RequestDetail = Awaited<ReturnType<typeof adminFeedbackApi.detail>>;

/** Approve / edit & approve / reject — only while a request is still pending. */
function ModerationPanel({ request, busy, onRun }: { request: RequestDetail; busy: boolean; onRun: Runner }) {
  const [title, setTitle] = useState(request.title);
  const [description, setDescription] = useState(request.description);
  const [category, setCategory] = useState(request.category ?? '');
  const [publicResponse, setPublicResponse] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [reason, setReason] = useState<string>(FEEDBACK_REJECTION_REASONS[0]);
  const [customerMessage, setCustomerMessage] = useState('');

  const edited = title !== request.title || description !== request.description;

  return (
    <WorkSection title="Moderate">
      <div className="mt-4 grid gap-4">
        <TextInput
          label="Title"
          hint="Tidy up the wording without changing what they asked for. The original is kept in the moderation record."
          value={title}
          maxLength={120}
          onChange={(event) => setTitle(event.target.value)}
        />
        <TextArea
          label="Description"
          rows={5}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        <Select
          label="Product area"
          value={category}
          options={[
            { value: '', label: 'Not set' },
            ...FEEDBACK_CATEGORIES.map((value) => ({ value, label: value.replace(/_/g, ' ') })),
          ]}
          onChange={(event) => setCategory(event.target.value)}
        />
        <TextArea
          label="Public response (optional)"
          hint="Shown on the request to every customer."
          rows={3}
          value={publicResponse}
          onChange={(event) => setPublicResponse(event.target.value)}
        />
        <TextArea
          label="Internal note (optional)"
          tone="internal"
          rows={2}
          value={internalNote}
          onChange={(event) => setInternalNote(event.target.value)}
        />

        <div>
          <Button
            variant="primary"
            disabled={busy}
            onClick={() =>
              onRun(
                () =>
                  adminFeedbackApi.approve(request.id, {
                    ...(edited ? { title: title.trim(), description: description.trim() } : {}),
                    category: category || null,
                    publicResponse: publicResponse.trim() || null,
                    internalNote: internalNote.trim() || null,
                  }),
                edited ? 'Edited and published to the board.' : 'Published to the board.',
              )
            }
          >
            {edited ? 'Edit & approve' : 'Approve'}
          </Button>
        </div>
      </div>

      <hr className="my-6 border-[color:var(--color-line)]" />

      <h3 className="font-semibold">Reject</h3>
      <div className="mt-3 grid gap-4">
        <Select
          label="Reason"
          value={reason}
          options={FEEDBACK_REJECTION_REASONS.map((value) => ({ value, label: value.replace(/_/g, ' ') }))}
          onChange={(event) => setReason(event.target.value)}
        />
        <TextArea
          label="Explanation for the customer (optional)"
          hint="Support questions and bug reports get a standard redirect when this is left blank."
          rows={3}
          value={customerMessage}
          onChange={(event) => setCustomerMessage(event.target.value)}
        />
        <div>
          <Button
            variant="danger"
            disabled={busy}
            onClick={() =>
              onRun(
                () =>
                  adminFeedbackApi.reject(request.id, {
                    reason,
                    customerMessage: customerMessage.trim() || null,
                    internalNote: internalNote.trim() || null,
                  }),
                'Rejected. The submitter has been told.',
              )
            }
          >
            Reject
          </Button>
        </div>
      </div>
    </WorkSection>
  );
}

/**
 * The development workflow (§17). One panel rather than a row of buttons,
 * because three of the five moves want a note beside them and two of them —
 * preview and release — require fields the customer will read.
 */
function LifecyclePanel({
  request,
  allowed,
  busy,
  onRun,
  onNavigate,
}: {
  request: RequestDetail;
  allowed: readonly FeedbackStatus[];
  busy: boolean;
  onRun: Runner;
  onNavigate: ReturnType<typeof useNavigate>;
}) {
  const [publicNote, setPublicNote] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [previewPath, setPreviewPath] = useState(request.previewPath ?? '');
  const [previewInstructions, setPreviewInstructions] = useState(request.previewInstructions ?? '');
  const [previewQuestion, setPreviewQuestion] = useState(request.previewQuestion ?? '');
  const [releaseVersion, setReleaseVersion] = useState(request.releaseVersion ?? '');
  const [releaseNotes, setReleaseNotes] = useState(request.releaseNotes ?? '');
  const [mergeTarget, setMergeTarget] = useState('');
  const [repository, setRepository] = useState(request.githubRepository ?? '');
  const [issueNumber, setIssueNumber] = useState(request.githubIssueNumber?.toString() ?? '');
  const [pullRequest, setPullRequest] = useState(request.githubPullRequest?.toString() ?? '');

  // Every status with a decision behind it has its own panel and its own
  // endpoint: approving stamps who and when, appends a moderation action and
  // tells the submitter; rejecting records a reason; merging and releasing
  // carry required fields. Offering them here as one-click moves was a way to
  // publish a request with no record of anybody having decided anything.
  const simpleMoves = allowed.filter(
    (status) =>
      status !== 'merged' &&
      status !== 'deployed' &&
      status !== 'preview' &&
      status !== 'rejected' &&
      status !== 'approved',
  );

  return (
    <>
      <WorkSection title="Development status">
        <p className="mt-1 text-xs text-[color:var(--color-muted)]">
          Currently {request.status.replace(/_/g, ' ')}. Every change is recorded with your name and the previous
          status.
        </p>

        <div className="mt-4 grid gap-4">
          <TextArea
            label="Public update (optional)"
            hint="Shown on the request. Do not promise a release date unless you mean it."
            rows={2}
            value={publicNote}
            onChange={(event) => setPublicNote(event.target.value)}
          />
          <TextArea
            label="Internal note (optional)"
            tone="internal"
            rows={2}
            value={internalNote}
            onChange={(event) => setInternalNote(event.target.value)}
          />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {simpleMoves.map((status) => (
            <Button
              key={status}
              disabled={busy}
              onClick={() =>
                onRun(
                  () =>
                    adminFeedbackApi.setStatus(request.id, {
                      status,
                      publicNote: publicNote.trim() || null,
                      internalNote: internalNote.trim() || null,
                    }),
                  `Moved to ${status.replace(/_/g, ' ')}.`,
                )
              }
            >
              {LABELS[status] ?? status.replace(/_/g, ' ')}
            </Button>
          ))}
          {simpleMoves.length === 0 ? (
            <p className="text-sm text-[color:var(--color-muted)]">
              No further status changes are available from here.
            </p>
          ) : null}
        </div>
      </WorkSection>

      {allowed.includes('preview') || request.status === 'preview' ? (
        <WorkSection title="Available for preview">
          <form
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              void onRun(
                () =>
                  adminFeedbackApi.setPreview(request.id, {
                    previewPath: previewPath.trim() || null,
                    previewInstructions: previewInstructions.trim() || null,
                    previewQuestion: previewQuestion.trim() || null,
                    publicNote: publicNote.trim() || null,
                  }),
                'Preview details saved.',
              );
            }}
          >
            <div className="mt-4 grid gap-4">
              <TextInput
                label="Preview path"
                hint="A path inside the app, e.g. /customers/reengagement. Blank sends previewers to the dashboard."
                value={previewPath}
                placeholder="/customers/reengagement"
                onChange={(event) => setPreviewPath(event.target.value)}
              />
              <TextArea
                label="Preview instructions"
                hint="What to try, in the customer's words."
                rows={3}
                value={previewInstructions}
                onChange={(event) => setPreviewInstructions(event.target.value)}
              />
              <TextInput
                label="Feedback question (optional)"
                value={previewQuestion}
                placeholder="Did the reminder wording feel right?"
                onChange={(event) => setPreviewQuestion(event.target.value)}
              />
              <div>
                <Button type="submit" variant="primary" disabled={busy}>
                  {request.status === 'preview' ? 'Save preview details' : 'Make available for preview'}
                </Button>
              </div>
            </div>
          </form>
        </WorkSection>
      ) : null}

      {allowed.includes('deployed') ? (
        <WorkSection title="Mark deployed">
          <form
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              void onRun(
                () =>
                  adminFeedbackApi.release(request.id, {
                    releaseVersion: releaseVersion.trim() || null,
                    releaseNotes: releaseNotes.trim() || null,
                    publicNote: publicNote.trim() || null,
                  }),
                'Marked deployed. It has moved to the Completed tab.',
              );
            }}
          >
            <div className="mt-4 grid gap-4">
              <TextInput
                label="Release version (optional)"
                value={releaseVersion}
                placeholder="2026.8"
                onChange={(event) => setReleaseVersion(event.target.value)}
              />
              <TextArea
                label="Release notes"
                hint="One short paragraph, written for the customers who voted for it."
                rows={3}
                value={releaseNotes}
                onChange={(event) => setReleaseNotes(event.target.value)}
              />
              <div>
                <Button type="submit" variant="primary" disabled={busy}>
                  Mark deployed
                </Button>
              </div>
            </div>
          </form>
        </WorkSection>
      ) : null}

      {allowed.includes('merged') ? (
        <WorkSection title="Merge into another request">
          <form
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              if (!mergeTarget.trim()) return;
              void onRun(async () => {
                const result = await adminFeedbackApi.merge(request.id, { canonicalRequestId: mergeTarget.trim() });
                onNavigate(`/admin/feedback/${result.canonicalRequestId}`);
              }, 'Merged.');
            }}
          >
            <div className="mt-4 grid gap-4">
              <TextInput
                label="Canonical request id"
                hint="Votes and followers move across; this request keeps its history and leaves the board."
                value={mergeTarget}
                onChange={(event) => setMergeTarget(event.target.value)}
              />
              <div>
                <Button type="submit" disabled={busy || !mergeTarget.trim()}>
                  Merge
                </Button>
              </div>
            </div>
          </form>
        </WorkSection>
      ) : null}

      <WorkSection title="Engineering links">
        <form
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            void onRun(
              () =>
                adminFeedbackApi.setLinks(request.id, {
                  githubRepository: repository.trim() || null,
                  githubIssueNumber: issueNumber.trim() ? Number.parseInt(issueNumber, 10) : null,
                  githubPullRequest: pullRequest.trim() ? Number.parseInt(pullRequest, 10) : null,
                }),
              'Engineering links saved.',
            );
          }}
        >
          <p className="mt-1 text-xs text-[color:var(--color-muted)]">
            Never shown to customers. For tracing an idea to the work that shipped it.
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <TextInput
              label="Repository"
              value={repository}
              placeholder="web-app"
              onChange={(event) => setRepository(event.target.value)}
            />
            <TextInput
              label="Issue number"
              value={issueNumber}
              inputMode="numeric"
              onChange={(event) => setIssueNumber(event.target.value)}
            />
            <TextInput
              label="Pull request"
              value={pullRequest}
              inputMode="numeric"
              onChange={(event) => setPullRequest(event.target.value)}
            />
          </div>
          <div className="mt-2">
            <Button type="submit" disabled={busy}>
              Save links
            </Button>
          </div>
        </form>
      </WorkSection>
    </>
  );
}

/** The operator-facing verbs from §17, rather than the raw status names. */
const LABELS: Partial<Record<FeedbackStatus, string>> = {
  under_consideration: 'Mark under consideration',
  planned: 'Mark planned',
  in_progress: 'Start development',
  archived: 'Archive',
};
